/* Smart Cart (sc-): cart drawer + product-page helpers. Runs only where sections/sc-smart-cart.liquid rendered
   (#sc-config present). Opens on cart:confirm (any add), on the header cart link (not on the cart page itself) and
   from the mobile sticky bar.
   - Optimistic UI: page Add buttons show "Adding…" / "Added ✓"; the drawer renders from the /cart/add.js response
     (read from a pass-through fetch observer, or event detail.added) and then reconciles with /cart.js.
   - Quantities / remove use window.MukhlisCartRules from cart.js (same add-on ≤ combo rules as the cart page).
   - Checkout: button.sc-pay[data-flexy-type="buynow"] inside [data-cart-drawer]; ga-cart-guard.js locks it (its
     capture-phase click blocker means our handler never runs while locked). It pops our history entry, then calls
     window.openFlexyCheckout() with NO arguments (whole cart; closing FlexyPe keeps the cart). Else /checkout.
   - Back button closes the drawer (pushState {sc:1}); ignored while #flexy-checkout exists.
   Events: sc:open, sc:add, sc:suggest_add, sc:checkout. Debug: ?sc_debug=1. */
(() => {
  const cfgEl = document.getElementById('sc-config');
  const root = document.querySelector('[data-sc]');
  if (!cfgEl || !root || window.__scMounted) return;
  window.__scMounted = true;
  let C;
  try {
    C = JSON.parse(cfgEl.textContent);
  } catch {
    return;
  }
  const R = C.routes || {};
  const S = C.settings || {};
  const DEBUG = new URLSearchParams(location.search).get('sc_debug') === '1';
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const desk = matchMedia('(min-width: 990px)');
  const docEl = document.documentElement;
  const P = () => window.MukhlisPricing;
  const RULES = () => window.MukhlisCartRules;
  const Brain = () => window.ScBrain;
  const $ = (s) => root.querySelector(s);

  const url = {
    cart: `${R.cart || '/cart'}.js`,
    add: `${R.add || '/cart/add'}.js`,
    change: `${R.change || '/cart/change'}.js`,
    update: `${R.update || '/cart/update'}.js`,
    checkout: `${String(R.root || '/').replace(/\/?$/, '/')}checkout`,
    product: (h) => `${String(R.root || '/').replace(/\/?$/, '/')}products/${encodeURIComponent(h)}.js`,
  };
  const cartPath = String(R.cart || '/cart').replace(/\/$/, '');
  const MAX_ADDS = Number.isFinite(Number(S.max_adds)) ? Number(S.max_adds) : 2;

  const panel = $('[data-sc-panel]');
  const body = $('[data-sc-body]');
  const linesEl = $('[data-sc-lines]');
  const emptyEl = $('[data-sc-empty]');
  const foot = $('[data-sc-foot]');
  const payBtn = $('[data-sc-pay]');
  const noticeEl = $('[data-sc-notice]');
  const liveEl = $('[data-sc-live]');
  const sugEl = $('[data-sc-sug]');
  const bar = document.querySelector('[data-sc-bar]');

  /* ---------- Helpers ---------- */
  const esc = (s) =>
    String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const money = (cents) => {
    const r = Math.round(Number(cents) || 0) / 100;
    try {
      return `₹${r.toLocaleString('en-IN', { minimumFractionDigits: r % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
    } catch {
      return `₹${r}`;
    }
  };
  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail }));
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));
  const sized = (src, w) => (src ? `${src}${src.includes('?') ? '&' : '?'}width=${w}` : '');
  const sget = (k, f) => {
    try {
      return JSON.parse(sessionStorage.getItem(k)) || f;
    } catch {
      return f;
    }
  };
  const sset = (k, v) => {
    try {
      sessionStorage.setItem(k, JSON.stringify(v));
    } catch {}
  };
  const sess = sget('sc_s1', null) || { n: 0, lines: [], adds: 0 };
  const saveSess = () => sset('sc_s1', sess);
  const log = (...a) => DEBUG && console.log('[Smart Cart]', ...a); // eslint-disable-line no-console
  // Never show the raw "Failed to fetch" / "Load failed"
  const friendly = (err, fallback) =>
    err instanceof TypeError || /failed to fetch|load failed|networkerror/i.test(err?.message || '')
      ? 'Network slow hai, dobara try karein'
      : err?.message || fallback;
  // Tasteful placeholder when a product has no image (ivory tile + small candle icon), never an empty box
  const PH =
    '<span class="sc-ph" aria-hidden="true"><svg viewBox="0 0 24 24" width="22" height="22" focusable="false"><path d="M12 3c1.6 2.2 2.4 3.6 2.4 4.8a2.4 2.4 0 0 1-4.8 0C9.6 6.6 10.4 5.2 12 3Z"/><rect x="8.5" y="11" width="7" height="10" rx="1.6"/></svg></span>';

  async function postJson(u, payload) {
    const res = await fetch(u, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.description || data.message || 'Could not update cart');
    return data;
  }
  async function fetchCart() {
    const res = await fetch(url.cart, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!res.ok) throw new Error('cart');
    return res.json();
  }

  /* ---------- Observe /cart/add.js responses (pass-through; nothing is changed) ---------- */
  let lastAdd = null; // { at, promise }
  try {
    const origFetch = window.fetch;
    if (typeof origFetch === 'function') {
      window.fetch = function scFetch(input, init) {
        const p = origFetch.apply(this, arguments);
        try {
          const raw = typeof input === 'string' ? input : input && input.url;
          const method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
          if (raw && method === 'POST' && /\/cart\/add(\.js)?$/.test(new URL(raw, location.href).pathname)) {
            const promise = p.then((res) => (res.ok ? res.clone().json() : null)).catch(() => null);
            lastAdd = { at: Date.now(), promise };
          }
        } catch {}
        return p;
      };
    }
  } catch {}
  const recentAdd = () =>
    lastAdd && Date.now() - lastAdd.at < 8000 ? Promise.race([lastAdd.promise, wait(400).then(() => null)]) : Promise.resolve(null);

  /* ---------- Cart state ---------- */
  let cart = null;
  let seq = 0;
  let selfEmit = false;
  let knownCount = null;
  const newKeys = new Set();
  const addedUntil = new Map(); // line key -> time until which the "✓ Added" highlight shows
  let reconcileT = 0;
  // One background /cart.js after a burst of changes; the UI never waits for it.
  const reconcileSoon = (ms = 1200) => {
    clearTimeout(reconcileT);
    reconcileT = setTimeout(refresh, ms);
  };
  const compare = new Map(Object.entries(C.compare || {}).map(([k, v]) => [Number(k), Number(v) || 0]));
  const compareAsked = new Set();

  const isAddon = (item) => (RULES() ? RULES().isAddonItem(item) : Boolean(item?.properties?.['Linked combo'] || item?.properties?._addon === '1'));
  const isTalbeena = (item) =>
    isAddon(item) || /talbeena|nutri-nuts|nutrinuts|takmeeli/.test(String(item.handle || '')) || /food/i.test(String(item.product_type || ''));
  const isCandle = (item) => /candle/i.test(String(item.product_type || ''));

  function updated(c) {
    selfEmit = true;
    try {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: c }));
    } finally {
      selfEmit = false;
    }
  }
  async function refresh() {
    const mine = ++seq;
    try {
      const c = await fetchCart();
      if (mine === seq) setCart(c);
      return c;
    } catch {
      return cart;
    }
  }
  function setCart(c, opts = {}) {
    if (!c || !Array.isArray(c.items)) return;
    if (!opts.optimistic) seq++;
    cart = c;
    askCompare();
    render();
    syncCount();
    syncProductUi();
    syncBar();
  }

  // Real compare-at prices for non-Talbeena lines (Liquid seed, catalogue, then /products/<handle>.js once).
  function askCompare() {
    (cart?.items || []).forEach((item) => {
      if (isTalbeena(item) || compare.has(item.variant_id) || compareAsked.has(item.handle)) return;
      compareAsked.add(item.handle);
      fetch(url.product(item.handle), { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((p) => {
          if (!p) return;
          (p.variants || []).forEach((v) => compare.set(Number(v.id), Number(v.compare_at_price) || 0));
          renderTotals();
        })
        .catch(() => {});
    });
  }

  /* ---------- Render ---------- */
  function totals(items) {
    let save = 0;
    const tal = items.filter(isTalbeena);
    if (tal.length && P()) save += P().cartTotals(tal).savings || 0;
    items
      .filter((i) => !isTalbeena(i))
      .forEach((i) => {
        const cmp = compare.get(Number(i.variant_id)) || 0;
        if (cmp > Number(i.price)) save += (cmp - Number(i.price)) * (Number(i.quantity) || 0);
      });
    return { sale: Number(cart?.total_price) || 0, save };
  }
  function renderTotals() {
    if (!cart) return;
    const t = totals(cart.items || []);
    $('[data-sc-subtotal]').textContent = money(t.sale);
    const saveEl = $('[data-sc-save]');
    saveEl.hidden = !(t.save > 0);
    saveEl.textContent = t.save > 0 ? `You save ${money(t.save)}` : '';
  }

  function lineHtml(item) {
    const props = item.properties || {};
    const addon = isAddon(item);
    const max = RULES() ? RULES().lineMax(cart, item) : Infinity;
    const q = qtyQueue.has(item.key) ? qtyQueue.get(item.key).q : Number(item.quantity) || 0;
    const title = item.product_title || item.title || '';
    let variant = '';
    if (props['Chosen flavours']) variant = props['Chosen flavours'];
    else if (item.variant_title && item.variant_title !== 'Default Title' && !props.Flavours && !props.Flavour) variant = item.variant_title;
    const safeProps = {};
    Object.keys(props).forEach((k) => {
      if (k === 'Chosen flavours') return;
      safeProps[esc(k)] = typeof props[k] === 'string' ? esc(props[k]) : props[k];
    });
    const propsHtml = P() ? P().propertiesHtml(safeProps) : '';
    const img = item.image
      ? `<img src="${esc(sized(item.image, 160))}" alt="" width="72" height="72" loading="lazy" decoding="async">`
      : PH;
    const shortTitle = Brain() ? Brain().shortName(title) : title;
    const justAdded = (addedUntil.get(item.key) || 0) > Date.now();
    // Optimistic placeholder while /cart/add.js is in flight: title + "Adding…", no controls yet
    if (item.pending) {
      return `<li class="sc-line is-new is-busy" data-sc-pending>
        <span class="sc-line__img">${img}</span>
        <div class="sc-line__info"><span class="sc-line__title">${esc(shortTitle)}</span><p class="sc-line__variant">Adding…</p></div>
        <div class="sc-line__price"></div>
      </li>`;
    }
    const priceHtml = P() ? P().linePriceHtml(item) : `<strong>${money(item.final_line_price)}</strong>`;
    return `<li class="sc-line${newKeys.has(item.key) ? ' is-new' : ''}${justAdded ? ' is-added' : ''}" data-sc-key="${esc(item.key)}">
      <a class="sc-line__img" href="${esc(item.url)}" tabindex="-1" aria-hidden="true">${img}</a>
      <div class="sc-line__info">
        <a class="sc-line__title" href="${esc(item.url)}">${esc(shortTitle)}</a>${addon ? '<span class="sc-line__tag">Add-on</span>' : ''}${justAdded ? '<span class="sc-line__added">✓ Added</span>' : ''}
        ${variant ? `<p class="sc-line__variant">${esc(variant)}</p>` : ''}
        ${propsHtml}
        <div class="sc-line__controls">
          <div class="sc-qty" role="group" aria-label="${esc(`Quantity of ${title}`)}">
            <button type="button" data-sc-minus aria-label="Decrease quantity">−</button>
            <output>${q}</output>
            <button type="button" data-sc-plus aria-label="Increase quantity"${q >= max ? ` disabled title="${addon ? 'One add-on per combo' : ''}"` : ''}>+</button>
          </div>
          <button type="button" class="sc-line__remove" data-sc-remove>${addon ? 'Remove add-on' : 'Remove'}</button>
        </div>
      </div>
      <div class="sc-line__price">${priceHtml}</div>
    </li>`;
  }

  function render() {
    if (!cart) return;
    const items = cart.items || [];
    $('[data-sc-count]').textContent = cart.item_count ? `(${cart.item_count})` : '';
    linesEl.innerHTML = items.map(lineHtml).join('');
    newKeys.clear();
    emptyEl.hidden = items.length > 0;
    foot.hidden = !items.length;
    $('[data-sc-prepaid]').hidden = !items.some(isCandle);
    renderTotals();
    renderSuggestions();
  }

  function syncCount() {
    const n = Number(cart?.item_count) || 0;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      if (root.contains(el)) return;
      el.textContent = String(n);
      el.hidden = !n;
      if (knownCount !== null && n !== knownCount && !reduce) {
        el.classList.remove('sc-bump');
        void el.offsetWidth;
        el.classList.add('sc-bump');
      }
    });
    knownCount = n;
  }

  let noticeT;
  function notice(msg) {
    if (!msg) return;
    noticeEl.textContent = msg;
    noticeEl.hidden = false;
    clearTimeout(noticeT);
    noticeT = setTimeout(() => {
      noticeEl.hidden = true;
      noticeEl.textContent = '';
    }, 5200);
  }
  const say = (msg) => {
    liveEl.textContent = '';
    setTimeout(() => (liveEl.textContent = msg), 30);
  };

  // "✓ Added" is a highlight + chip on the added line itself (never an overlay covering it), for 2 s.
  function markAdded(keys) {
    const until = Date.now() + 2000;
    keys.forEach((k) => k && addedUntil.set(k, until));
    setTimeout(() => {
      linesEl.querySelectorAll('.sc-line.is-added').forEach((li) => {
        if ((addedUntil.get(li.dataset.scKey) || 0) > Date.now()) return;
        li.classList.remove('is-added');
        li.querySelector('.sc-line__added')?.remove();
      });
    }, 2050);
  }
  function showAdded(name) {
    if (name) say(`Added: ${name}`);
  }

  /* ---------- Quantity / remove / undo ---------- */
  // Taps update the number at once; the final quantity is sent ONCE, 300 ms after the last tap. The add-on ≤ combo
  // rules (cart.js planQuantity) are checked against the cart we already have, so there is no /cart.js before the POST.
  const qtyQueue = new Map(); // key -> { q, remove, t }
  const shownQty = (key, item) => (qtyQueue.has(key) ? qtyQueue.get(key).q : Number(item.quantity) || 0);

  function queueQty(key, q, remove) {
    const item = (cart?.items || []).find((i) => i.key === key);
    if (!item) return;
    const next = Math.max(0, q);
    const prev = qtyQueue.get(key);
    clearTimeout(prev?.t);
    const li = linesEl.querySelector(`[data-sc-key="${CSS.escape(key)}"]`);
    const out = li?.querySelector('output');
    if (out) out.textContent = String(next);
    const plus = li?.querySelector('[data-sc-plus]');
    if (plus && RULES()) plus.disabled = next >= RULES().lineMax(cart, item);
    const entry = { q: next, remove: remove || next === 0 };
    entry.t = setTimeout(() => flushQty(key), remove ? 0 : 300);
    qtyQueue.set(key, entry);
  }

  async function flushQty(key) {
    const entry = qtyQueue.get(key);
    qtyQueue.delete(key);
    if (!entry || !cart) return;
    const li = linesEl.querySelector(`[data-sc-key="${CSS.escape(key)}"]`);
    const plan = RULES()
      ? RULES().planQuantity(cart, key, entry.q)
      : { type: cart.items.some((i) => i.key === key) ? 'change' : 'missing', qty: entry.q, item: cart.items.find((i) => i.key === key) };
    if (plan.type === 'missing') return refresh();
    if (plan.notice && !plan.noticeAfter) notice(plan.notice);
    if (plan.type === 'none' || Number(plan.item?.quantity) === plan.qty) return render(); // nothing to send; show the real qty
    li?.classList.add('is-busy');
    try {
      const next =
        plan.type === 'update'
          ? await postJson(url.update, { updates: plan.updates })
          : await postJson(url.change, { id: key, quantity: plan.qty });
      if (plan.notice && plan.noticeAfter) notice(plan.notice);
      if (entry.remove && plan.qty === 0) {
        const it = plan.item;
        showUndo(`Removed ${Brain() ? Brain().shortName(it.product_title) : it.product_title}`, async () => {
          const added = await postJson(url.add, { items: [{ id: it.variant_id, quantity: it.quantity, properties: it.properties || {} }] });
          (added.items || []).forEach((i) => newKeys.add(i.key));
          say(`${it.product_title} is back in your cart`);
        });
      }
      setCart(next);
      updated(next);
    } catch (err) {
      notice(friendly(err, 'Could not update cart. Please try again.'));
      render();
    }
  }

  // 5 s toast with Undo (remove, upgrade). onUndo does the requests; the cart is reloaded afterwards.
  let undoBox = null;
  function showUndo(text, onUndo) {
    undoBox?.remove();
    const box = document.createElement('div');
    box.className = 'sc-undo';
    box.setAttribute('role', 'status');
    box.innerHTML = `<span>${esc(text)}</span><button type="button">Undo</button>`;
    linesEl.before(box);
    undoBox = box;
    const t = setTimeout(() => box.remove(), 5000);
    box.querySelector('button').addEventListener('click', async () => {
      clearTimeout(t);
      box.querySelector('button').disabled = true;
      try {
        await onUndo();
        box.remove();
        const c = await refresh();
        updated(c);
      } catch (err) {
        box.remove();
        notice(friendly(err, 'Could not undo. Please try again.'));
        refresh();
      }
    });
  }

  linesEl.addEventListener('click', (e) => {
    const li = e.target.closest('[data-sc-key]');
    if (!li || li.classList.contains('is-busy')) return;
    const key = li.dataset.scKey;
    const item = (cart?.items || []).find((i) => i.key === key);
    if (!item) return;
    const q = shownQty(key, item);
    if (e.target.closest('[data-sc-minus]')) queueQty(key, q - 1, q - 1 === 0);
    else if (e.target.closest('[data-sc-plus]')) queueQty(key, q + 1, false);
    else if (e.target.closest('[data-sc-remove]')) queueQty(key, 0, true);
  });

  /* ---------- Suggestions ---------- */
  let catalog = null;
  let catalogP = null;
  let picks = [];
  let lineCache = null; // { top, line } keeps the message stable while the top pick stays the same
  let countedOpen = false;
  const CAT_KEY = 'sc_cat_v1';

  function loadCatalog() {
    if (catalogP) return catalogP;
    const cached = sget(CAT_KEY, null);
    if (cached && Array.isArray(cached.products) && Date.now() - cached.t < 30 * 60 * 1000) {
      catalog = cached.products;
      catalogP = Promise.resolve(catalog);
      return catalogP;
    }
    catalogP = (async () => {
      const all = [];
      for (let page = 1; page <= 8; page += 1) {
        const res = await fetch(`${R.catalog}&page=${page}`, { credentials: 'same-origin' });
        if (!res.ok) break;
        const data = JSON.parse(await res.text());
        all.push(...(data.products || []));
        if (!(data.pages > page)) break;
      }
      catalog = all;
      sset(CAT_KEY, { t: Date.now(), products: all });
      all.forEach((p) => (p.variants || []).forEach((v) => !compare.has(Number(v.id)) && compare.set(Number(v.id), Number(v.compare_at_price) || 0)));
      return all;
    })().catch(() => {
      catalogP = null;
      return null;
    });
    return catalogP;
  }

  function priceHtml(price, cmp) {
    const B = Brain();
    const s = B ? B.saving({ price, compare: cmp }) : 0;
    return `<b>${money(price)}</b>${s ? `<s>${money(cmp)}</s><span class="sc-card__save">Save ${money(s)}</span>` : ''}`;
  }
  function chipsHtml(r) {
    return r.chips
      .map(
        (o, i) => `<div class="sc-opt" role="group" aria-label="${esc(o.label)}">
          <span class="sc-opt__name">${esc(o.label)}</span>
          <div class="sc-chips">${o.values
            .map((v) => `<button type="button" class="sc-chip" data-sc-chip="${esc(r.p.handle)}" data-i="${i}" data-v="${esc(v)}" aria-pressed="${r.sel[i] === v}">${esc(v)}</button>`)
            .join('')}</div>
        </div>`
      )
      .join('');
  }
  function addLabel(r) {
    if (r.kind === 'upgrade') return { text: `Upgrade · +${money(Math.max(0, r.diff))}`, ok: true };
    if (r.kind !== 'chips') return { text: '+ Add', ok: true };
    const missing = r.chips.findIndex((o, i) => !r.sel[i]);
    if (missing >= 0) return { text: `Choose ${r.chips[missing].label}`, ok: false };
    const v = Brain().resolveVariant(r.p, r.sel);
    if (!v || !v.available) return { text: 'Not available', ok: false };
    return { text: '+ Add', ok: true, v };
  }
  function cardHtml(r, main) {
    const { p, t } = r;
    const B = Brain();
    const h = esc(p.handle);
    const href = esc(r.kind === 'atelier' ? C.atelier_url : p.url);
    const img = p.image ? `<img src="${esc(p.image)}" alt="${esc(p.image_alt || p.title)}" width="96" height="96" loading="lazy" decoding="async">` : PH;
    let act;
    if (r.kind === 'add' || r.kind === 'chips' || r.kind === 'upgrade') {
      const l = addLabel(r);
      act = `<button type="button" class="sc-add" data-sc-add="${h}"${l.ok ? '' : ' disabled'}>${esc(l.text)}</button>`;
    } else {
      const label = r.kind === 'flavours' ? 'Choose flavours →' : r.kind === 'atelier' ? 'Apna box banao →' : 'View →';
      act = `<a class="sc-link" href="${href}" data-sc-to="${h}">${label}</a>`;
    }
    const v = r.kind === 'chips' ? addLabel(r).v : null;
    return `<div class="sc-card${main ? ' sc-card--main' : ''}${r.isNew ? ' is-new' : ''}" data-sc-item="${h}">
      <a class="sc-card__img" href="${href}" data-sc-to="${h}" tabindex="-1" aria-hidden="true">${img}</a>
      <div class="sc-card__body">
        <a class="sc-card__name" href="${href}" data-sc-to="${h}">${esc(B.shortName(p.title))}</a>
        <span class="sc-card__price" data-sc-price>${v ? priceHtml(v.price, v.compare_at_price) : priceHtml(t.price, t.compare)}</span>
      </div>
      ${r.kind === 'chips' ? `<div class="sc-card__opts">${chipsHtml(r)}</div>` : ''}
      <div class="sc-card__act">${act}</div>
    </div>`;
  }

  function renderSuggestions() {
    const B = Brain();
    const items = cart?.items || [];
    if (!items.length || !catalog || !B || sess.adds >= MAX_ADDS) {
      sugEl.hidden = true;
      return;
    }
    const prevSel = new Map(picks.map((r) => [r.p.handle, r.sel]));
    const prevHandles = new Set(picks.map((r) => r.p.handle));
    const res = B.suggest({ catalog, cart, settings: S, sess, max: 3, atelierUrl: C.atelier_url });
    picks = res.picks;
    picks.forEach((r) => {
      if (prevSel.has(r.p.handle)) r.sel = prevSel.get(r.p.handle);
      else if (r.chips) r.chips.forEach((o, i) => o.values.length === 1 && (r.sel[i] = o.values[0]));
      r.isNew = prevHandles.size > 0 && !prevHandles.has(r.p.handle);
    });
    if (DEBUG) {
      console.groupCollapsed(`[Smart Cart] picks: ${picks.map((r) => `${r.p.handle} (${r.kind})`).join(', ') || '(none)'}`); // eslint-disable-line no-console
      console.table((res.rows || []).map((r) => ({ handle: r.p.handle, score: r.score, kind: r.kind, anchor: r.anchor.t.handle, why: r.why.join(' | ') }))); // eslint-disable-line no-console
      console.table(res.skipped); // eslint-disable-line no-console
      console.groupEnd(); // eslint-disable-line no-console
    }
    if (!picks.length) {
      sugEl.hidden = true;
      return;
    }
    const top = picks[0];
    if (!lineCache || lineCache.top !== top.p.handle) lineCache = { top: top.p.handle, line: res.line };
    const line = lineCache.line;
    const msg = $('[data-sc-sug-msg]');
    msg.textContent = line ? line.text : 'Saath mein kuch aur? ✨';
    msg.lang = line ? B.LANG_ATTR[line.lang] || 'en' : 'hi-Latn';
    msg.classList.toggle('sc--hi', line?.lang === 'hindi');

    $('[data-sc-sug-main]').innerHTML = cardHtml(top, true);
    const rest = picks.slice(1, 3);
    const toggle = $('[data-sc-sug-toggle]');
    const restEl = $('[data-sc-sug-rest]');
    restEl.innerHTML = rest.map((r) => cardHtml(r, false)).join('');
    toggle.hidden = !rest.length;
    toggle.textContent = `${rest.length} more that pair${rest.length === 1 ? 's' : ''} well`;
    if (!rest.length) restEl.hidden = true;
    sugEl.hidden = false;

    if (isOpen && !countedOpen) {
      countedOpen = true;
      picks.forEach((r) => B.pair(r.anchor.t.handle, r.p.handle).s++);
      if (line) {
        B.visitor.lines[line.id] = (B.visitor.lines[line.id] || 0) + 1;
        sess.lines.push(line.id);
        if (sess.lines.length > 30) sess.lines.shift();
      }
      sess.n++;
      B.saveVisitor();
      saveSess();
    }
  }

  $('[data-sc-sug-toggle]').addEventListener('click', (e) => {
    const restEl = $('[data-sc-sug-rest]');
    const open = e.currentTarget.getAttribute('aria-expanded') !== 'true';
    e.currentTarget.setAttribute('aria-expanded', String(open));
    restEl.hidden = !open;
  });

  sugEl.addEventListener('click', (e) => {
    const B = Brain();
    const chip = e.target.closest('[data-sc-chip]');
    if (chip) {
      const r = picks.find((x) => x.p.handle === chip.dataset.scChip);
      if (!r) return;
      r.sel[Number(chip.dataset.i)] = chip.dataset.v;
      chip.parentElement.querySelectorAll('[data-sc-chip]').forEach((b) => b.setAttribute('aria-pressed', String(b === chip)));
      const card = chip.closest('[data-sc-item]');
      const l = addLabel(r);
      const btn = card.querySelector('[data-sc-add]');
      btn.disabled = !l.ok;
      btn.textContent = l.text;
      if (l.v) card.querySelector('[data-sc-price]').innerHTML = priceHtml(l.v.price, l.v.compare_at_price);
      return;
    }
    const link = e.target.closest('[data-sc-to]');
    if (link && B) {
      const r = picks.find((x) => x.p.handle === link.dataset.scTo);
      if (r) {
        B.pair(r.anchor.t.handle, r.p.handle).c++;
        B.visitor.clicked[r.p.handle] = r.anchor.t.handle;
        B.saveVisitor();
      }
      return;
    }
    const addBtn = e.target.closest('[data-sc-add]');
    if (addBtn && !addBtn.disabled) addSuggestion(addBtn);
  });

  async function addSuggestion(btn) {
    const B = Brain();
    const r = picks.find((x) => x.p.handle === btn.dataset.scAdd);
    if (!r || sess.adds >= MAX_ADDS) return;
    const v = r.kind === 'chips' ? addLabel(r).v : null;
    const id = Number(v ? v.id : r.p.variant_id);
    if (!id) return;
    const price = v ? v.price : r.t.price;
    const idle = btn.textContent;
    btn.disabled = true;
    btn.textContent = r.kind === 'upgrade' ? 'Upgrading…' : 'Adding…';
    try {
      let added;
      if (r.kind === 'upgrade') {
        added = await upgradeLine(r, id);
        if (!added) return; // the line to replace is gone; cart reloaded
      } else {
        added = await postJson(url.add, { items: [{ id, quantity: 1 }] });
      }
      (added.items || []).forEach((i) => newKeys.add(i.key));
      markAdded((added.items || []).map((i) => i.key));
      sess.adds++;
      saveSess();
      B.pair(r.anchor.t.handle, r.p.handle).a++;
      B.saveVisitor();
      const detail = { from: r.anchor.t.handle, to: r.p.handle, variant_id: id, value: price / 100, currency: 'INR' };
      emit('sc:suggest_add', detail);
      emit('sc:add', { ...detail, via: 'suggestion' });
      if (typeof window.fbq === 'function') {
        try {
          window.fbq('trackCustom', 'SmartCartAdd', detail);
        } catch {}
      }
      btn.closest('[data-sc-item]')?.classList.add('is-leaving');
      say(`${r.kind === 'upgrade' ? 'Upgraded to' : 'Added:'} ${B.shortName(r.p.title)}`);
      await wait(reduce ? 0 : 260);
      const c = await refresh();
      updated(c);
    } catch (err) {
      btn.disabled = false;
      btn.textContent = idle;
      notice(friendly(err, 'Could not add. Please try again.'));
      refresh();
    }
  }

  // Upgrade = replace the old line in one flow: old line qty 0, then the upgrade with the same quantity.
  // If the add fails, the old line is put back. A 5 s "Upgraded ✓ · Undo" restores the old line.
  async function upgradeLine(r, id) {
    const old = (cart?.items || []).find((i) => i.key === r.replace?.key);
    if (!old) {
      await refresh();
      return null;
    }
    const restoreOld = () =>
      postJson(url.add, { items: [{ id: old.variant_id, quantity: old.quantity, properties: old.properties || {} }] });
    await postJson(url.change, { id: old.key, quantity: 0 });
    let added;
    try {
      added = await postJson(url.add, { items: [{ id, quantity: Number(old.quantity) || 1 }] });
    } catch (err) {
      await restoreOld().catch(() => {});
      throw err;
    }
    const newKey = (added.items || [])[0]?.key;
    showUndo(`Upgraded ✓ · ${Brain().shortName(r.p.title)}`, async () => {
      if (newKey) await postJson(url.change, { id: newKey, quantity: 0 });
      const back = await restoreOld();
      (back.items || []).forEach((i) => newKeys.add(i.key));
      say(`${old.product_title} is back in your cart`);
    });
    return added;
  }

  /* ---------- Open / close, history, focus ---------- */
  let isOpen = false;
  let pushed = false;
  let ignorePop = 0;
  let lastFocus = null;
  let userClosedAt = 0; // a closed drawer is never reopened by a late add response

  function open(reason, opts = {}) {
    if (opts.addedName) showAdded(opts.addedName);
    if (isOpen) return;
    isOpen = true;
    countedOpen = false;
    lastFocus = document.activeElement;
    root.hidden = false;
    docEl.classList.add('sc-lock');
    panel.style.transform = '';
    requestAnimationFrame(() => requestAnimationFrame(() => root.classList.add('is-open')));
    panel.focus({ preventScroll: true });
    try {
      history.pushState({ sc: 1 }, '');
      pushed = true;
    } catch {}
    emit('sc:open', { reason });
    if (!cart) refresh();
    loadCatalog().then(() => {
      if (catalog && cart && Brain()) {
        Brain().creditAdds(cart, (from, to) => emit('sc:add', { from, to, via: 'link' }));
        renderSuggestions();
      }
    });
  }
  function close({ fromPop = false, restoreFocus = true, user = true } = {}) {
    if (!isOpen) return;
    isOpen = false;
    if (user) userClosedAt = Date.now();
    root.classList.remove('is-open');
    docEl.classList.remove('sc-lock');
    setTimeout(() => {
      if (!isOpen) root.hidden = true;
    }, reduce ? 0 : 320);
    if (pushed && !fromPop && history.state && history.state.sc) {
      ignorePop++;
      history.back();
    }
    pushed = false;
    if (restoreFocus && lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus({ preventScroll: true });
  }
  addEventListener('popstate', () => {
    if (ignorePop > 0) {
      ignorePop--;
      return;
    }
    if (document.getElementById('flexy-checkout')) return;
    if (isOpen) {
      pushed = false;
      close({ fromPop: true });
    }
  });

  root.addEventListener('click', (e) => {
    if (e.target.closest('[data-sc-close]')) close();
  });
  root.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') return close();
    if (e.key !== 'Tab') return;
    const f = [...panel.querySelectorAll('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])')].filter(
      (el) => el.offsetParent !== null
    );
    if (!f.length) return;
    const first = f[0];
    const last = f[f.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  // Swipe down to close (mobile sheet): from the handle / header, or anywhere while the list is scrolled to the top.
  let y0 = null;
  let dy = 0;
  panel.addEventListener(
    'touchstart',
    (e) => {
      if (desk.matches || e.touches.length !== 1) return;
      const onGrab = e.target.closest('[data-sc-grab]');
      if (!onGrab && (body.scrollTop > 0 || !body.contains(e.target))) return;
      if (e.target.closest('button, a, input, .sc-chips')) return;
      y0 = e.touches[0].clientY;
      dy = 0;
    },
    { passive: true }
  );
  panel.addEventListener(
    'touchmove',
    (e) => {
      if (y0 === null) return;
      dy = e.touches[0].clientY - y0;
      if (dy <= 0 || body.scrollTop > 0) {
        if (dy < 0) y0 = null;
        panel.classList.remove('is-drag');
        panel.style.transform = '';
        return;
      }
      panel.classList.add('is-drag');
      panel.style.transform = `translateY(${dy}px)`;
    },
    { passive: true }
  );
  const endDrag = () => {
    if (y0 === null) return;
    y0 = null;
    panel.classList.remove('is-drag');
    if (dy > 90) close();
    else panel.style.transform = '';
  };
  panel.addEventListener('touchend', endDrag);
  panel.addEventListener('touchcancel', endDrag);

  /* ---------- Checkout ---------- */
  const flexyOn = () => typeof window.openFlexyCheckout === 'function' && window.flexypeCheckoutActive === true;
  // Same readiness check as product-form.js; waits up to 3 s while FlexyPe has not decided yet.
  const flexyReady = () =>
    typeof window.flexypeCheckoutActive === 'boolean'
      ? Promise.resolve(flexyOn())
      : new Promise((resolve) => {
          const t0 = Date.now();
          const iv = setInterval(() => {
            if (typeof window.flexypeCheckoutActive === 'boolean' || Date.now() - t0 > 3000) {
              clearInterval(iv);
              resolve(flexyOn());
            }
          }, 100);
        });
  payBtn.addEventListener('click', async () => {
    if (payBtn.disabled || payBtn.classList.contains('ga-guard-locked')) return; // ga-cart-guard also blocks in capture
    if (payBtn.getAttribute('aria-busy') === 'true') return;
    payBtn.setAttribute('aria-busy', 'true');
    const ready = await flexyReady();
    payBtn.removeAttribute('aria-busy');
    emit('sc:checkout', { item_count: cart?.item_count || 0, total: (cart?.total_price || 0) / 100, via: ready ? 'flexype' : 'native' });
    if (!ready) {
      location.href = url.checkout;
      return;
    }
    const go = () => {
      try {
        window.openFlexyCheckout();
      } catch {
        location.href = url.checkout;
      }
    };
    if (isOpen && pushed && history.state && history.state.sc) {
      // Pop our history entry first, so FlexyPe starts from the page's own entry.
      pushed = false;
      ignorePop++;
      let done = false;
      const fire = () => {
        if (done) return;
        done = true;
        go();
      };
      addEventListener('popstate', fire, { once: true });
      setTimeout(fire, 400);
      history.back();
      close({ restoreFocus: false, user: false });
    } else {
      close({ restoreFocus: false, user: false });
      go();
    }
  });

  /* ---------- Page add buttons: optimistic labels ---------- */
  const ADD_SEL =
    '[data-add-to-cart], [data-mobile-add], [data-fx-add], [data-gg-add], [data-st-add], [data-gm-add-to-cart], [data-gm-product-form] [type="submit"], [data-gm-product-form] [name="add"]';
  const LABEL_SEL = '.fx-btn__label, .gg-btn__label, .st-btn__label';
  const labelOf = (btn) => btn.querySelector(LABEL_SEL) || (btn.children.length ? null : btn);
  let pending = null; // { btn, label, original, mirror, done, t0 }
  const holding = new Set(); // buttons showing "Added ✓": syncProductUi must not relabel them yet (no flicker)

  // Open the drawer the moment the page's own add starts, with a placeholder line from the page's product;
  // the real line replaces it when /cart/add.js answers.
  function openOptimistic(p) {
    if (!C.product || !isPrimary(p.btn, p)) return;
    const base = cart ? { ...cart, items: cart.items.filter((i) => !i.pending) } : { items: [], item_count: 0, total_price: 0 };
    base.items.unshift({ pending: true, key: 'sc-pending', product_title: C.product.title, image: C.product.image || '', quantity: 1, properties: {} });
    setCart(base, { optimistic: true });
    open('add');
  }
  const isPrimary = (btn, p) => !!p.mirror || btn.matches(PRIMARY_SEL);
  const dropPending = () => {
    if (!cart || !cart.items.some((i) => i.pending)) return;
    setCart({ ...cart, items: cart.items.filter((i) => !i.pending) }, { optimistic: true });
  };
  // The page's own error message (product form error or fx/gg/st toast), else the friendly network line
  const pageError = () => {
    const el = document.querySelector('[data-form-error]:not([hidden]), .fx-toast.is-on, .gg-toast.is-on, .st-toast.is-on');
    const text = el && el.textContent.trim();
    return text && !/failed to fetch|load failed/i.test(text) ? text : 'Network slow hai, dobara try karein';
  };

  function setLabel(p, text) {
    if (p.label) p.label.textContent = text;
    if (p.mirror) p.mirror.textContent = text;
  }
  function restore(p) {
    if (p.label && p.original != null) p.label.textContent = p.original;
    if (p.mirror) syncBar(true);
    syncProductUi();
  }
  document.addEventListener(
    'click',
    (e) => {
      const btn = e.target.closest?.(ADD_SEL);
      if (!btn || root.contains(btn) || btn.disabled) return;
      const label = labelOf(btn);
      const p = { btn, label, original: label ? label.dataset.scOrig || label.textContent : null, mirror: barMirror, done: false, t0: Date.now() };
      barMirror = null;
      pending = p;
      setTimeout(() => {
        if (pending !== p) return;
        if (!btn.disabled) {
          pending = null; // validation stopped it (Talbeena flavours etc.)
          if (p.mirror) syncBar(true);
          return;
        }
        setLabel(p, 'Adding…');
        openOptimistic(p);
        const iv = setInterval(() => {
          if (p.done || pending !== p) return clearInterval(iv);
          if (!btn.disabled || Date.now() - p.t0 > 20000) {
            clearInterval(iv);
            if (pending === p) pending = null;
            restore(p); // failed: buttons recover; the drawer drops the placeholder and says why
            if (cart && cart.items.some((i) => i.pending)) {
              dropPending();
              notice(pageError());
            }
          }
        }, 250);
      }, 0);
    },
    true
  );

  document.addEventListener('cart:confirm', async (e) => {
    const d = e.detail || {};
    const p = pending;
    pending = null;
    if (p) {
      p.done = true;
      holding.add(p.btn);
    }
    const added = d.added || (await recentAdd());
    let name = '';
    if (added) {
      const items = Array.isArray(added.items) ? added.items : [added];
      name = items[0]?.product_title || '';
      items.forEach((i) => i && i.key && newKeys.add(i.key));
      markAdded(items.map((i) => i && i.key));
      const base = cart ? { ...cart, items: cart.items.filter((i) => !i.pending) } : { items: [], item_count: 0, total_price: 0 };
      items.forEach((it) => {
        if (!it || !it.key) return;
        const idx = base.items.findIndex((x) => x.key === it.key);
        if (idx >= 0) base.items[idx] = { ...base.items[idx], ...it, quantity: Math.max(Number(base.items[idx].quantity) || 0, Number(it.quantity) || 0) };
        else base.items.unshift(it);
      });
      base.item_count = base.items.reduce((s, i) => s + (Number(i.quantity) || 0), 0);
      base.total_price = base.items.reduce((s, i) => s + (Number(i.final_line_price) || 0), 0);
      setCart(base, { optimistic: true });
      reconcileSoon(); // totals / discounts double-checked in the background
    } else {
      dropPending();
      refresh();
    }
    if (!name) name = String(d.message || '').replace(/ added to cart\.?$/i, '').split(' — ')[0];
    emit('sc:add', { via: 'page', product: name });
    // Already open (optimistic) → just announce. Closed by the customer meanwhile → stay closed.
    const startedAt = p ? p.t0 : Date.now() - 1;
    if (isOpen) showAdded(name);
    else if (userClosedAt < startedAt) open('add', { addedName: name });
    if (p) {
      await null; // the page's own finally{} restores its label synchronously after dispatching; switch right after (no frame between)
      setLabel(p, 'Added ✓');
      await wait(600);
      holding.delete(p.btn);
      restore(p);
    }
  });

  /* ---------- Header cart link ---------- */
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = e.target.closest?.('a[href]');
    if (!a || root.contains(a) || a.closest('[data-cart-confirm]')) return;
    let u;
    try {
      u = new URL(a.href, location.href);
    } catch {
      return;
    }
    if (u.origin !== location.origin || u.pathname.replace(/\/$/, '') !== cartPath) return;
    if (location.pathname.replace(/\/$/, '') === cartPath) return;
    e.preventDefault();
    open('header');
  });

  /* ---------- Product page: "Add another" + "In cart (N) · View cart" ---------- */
  const PRIMARY_SEL =
    '[data-product-root] [data-add-to-cart], [data-product-root] [data-mobile-add], [data-fx] .fx-btn--buy[data-fx-add], [data-gg] .gg-btn--buy[data-gg-add], [data-st] [data-st-add]';
  const HERO_SEL = '[data-product-root] .cta-stack--desktop, [data-fx-hero-cta], [data-gg-hero-cta], [data-st-hero-cta]';
  const inCartQty = () =>
    C.product ? (cart?.items || []).filter((i) => Number(i.product_id) === Number(C.product.id)).reduce((s, i) => s + (Number(i.quantity) || 0), 0) : 0;

  function syncProductUi() {
    if (!C.product || !cart) return;
    const n = inCartQty();
    document.querySelectorAll(PRIMARY_SEL).forEach((btn) => {
      if ((pending && pending.btn === btn) || holding.has(btn)) return;
      const label = labelOf(btn);
      if (!label) return;
      if (label.dataset.scOrig == null) {
        if (label.textContent.trim().toLowerCase() !== 'add to cart') return;
        label.dataset.scOrig = label.textContent;
      }
      label.textContent = n > 0 ? 'Add another' : label.dataset.scOrig;
    });
    document.querySelectorAll(HERO_SEL).forEach((hero) => {
      let link = hero.nextElementSibling;
      if (!link || !link.matches('[data-sc-incart]')) {
        if (!n) return;
        link = document.createElement('a');
        link.className = 'sc-incart';
        link.href = R.cart || '/cart';
        link.setAttribute('data-sc-incart', '');
        hero.after(link);
      }
      link.hidden = !n;
      link.textContent = `In cart (${n}) · View cart`;
    });
  }

  /* ---------- Mobile sticky bar ---------- */
  let barMirror = null;
  const pageAddBtn = () => {
    const sels = [
      '[data-product-root] [data-mobile-add]',
      '[data-product-root] [data-add-to-cart]',
      '[data-fx] .fx-btn--buy[data-fx-add]',
      '[data-fx] [data-fx-add]',
      '[data-gg] .gg-btn--buy[data-gg-add]',
      '[data-gg] [data-gg-add]',
      '[data-st] [data-st-add]',
      '[data-gm-product-form] [type="submit"]',
    ];
    for (const s of sels) {
      const el = document.querySelector(s);
      if (el && !root.contains(el)) return el;
    }
    return null;
  };
  const isMainForm = () => !!document.querySelector('[data-product-root] [data-add-to-cart]');
  const livePriceEl = document.querySelector('[data-product-root] [data-product-price][data-price]');
  const barPrice = () => Number(livePriceEl?.dataset.price) || Number(C.product?.price) || 0;

  function syncBar(force) {
    if (!bar || !cart) return;
    if (pending && pending.mirror && !force) return;
    const n = Number(cart.item_count) || 0;
    const add = pageAddBtn();
    const emptyState = bar.querySelector('[data-sc-bar-empty]');
    const fullState = bar.querySelector('[data-sc-bar-full]');
    // The bar is never hidden by an observer: it only steps aside when there is nothing to offer
    // (no Add button on the page and an empty cart).
    if (!n && !add) {
      bar.hidden = true;
      docEl.classList.remove('sc-bar-on');
      return;
    }
    bar.hidden = false;
    docEl.classList.add('sc-bar-on');
    // main-product pages (Talbeena, Nutri Nuts, Care, Stickers…): the bar replaces the old mobile bar, so it ALWAYS
    // offers Add to cart, plus a compact "🛒 N" (opens the drawer, Checkout is there) once the cart has items.
    // fx / gg / st pages keep their own visible hero button, so they keep "🛒 N · ₹total" + "Checkout →".
    const keepAdd = !!add && isMainForm();
    const showAdd = !n || keepAdd;
    emptyState.hidden = !showAdd;
    fullState.hidden = showAdd;
    const mini = bar.querySelector('[data-sc-bar-mini]');
    if (mini) {
      mini.hidden = !(showAdd && n > 0);
      bar.querySelector('[data-sc-bar-mini-count]').textContent = String(n);
    }
    const addBtn = bar.querySelector('[data-sc-bar-add]');
    const price = barPrice();
    addBtn.innerHTML = `${inCartQty() ? 'Add another' : 'Add to cart'}${price ? ` · <b>${money(price)}</b>` : ''}`;
    addBtn.disabled = !!add?.disabled && !(pending && pending.btn === add);
    bar.querySelector('[data-sc-bar-count]').textContent = String(n);
    bar.querySelector('[data-sc-bar-total]').textContent = money(cart.total_price);
    // Takmeeli clearance only: mirror the page's "+N FREE (₹…)" line (product-form.js keeps it on the selected combo).
    const freeEl = bar.querySelector('[data-sc-bar-free]');
    if (freeEl) {
      const freeText = document.querySelector('[data-product-root] [data-mobile-free]')?.textContent.trim() || '';
      freeEl.textContent = freeText;
      freeEl.hidden = !freeText;
      docEl.classList.toggle('sc-bar-free', !!freeText);
    }
  }

  if (bar) {
    bar.querySelector('[data-sc-bar-add]').addEventListener('click', (e) => {
      const add = pageAddBtn();
      if (!add) return;
      if (add.disabled) {
        add.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
        return;
      }
      barMirror = e.currentTarget;
      add.click(); // the page's own handler: Talbeena flavour validation and add-on lines stay exactly as they are
      barMirror = null;
      // Validation failed (nothing is adding) → bring the page's error (e.g. "Please select 3 flavours") into view.
      // Poll briefly: on slow phones the error can render a little after the click.
      const t0 = Date.now();
      const findErr = () => {
        if (add.disabled) return; // the add is running, not an error
        const err = document.querySelector('[data-product-root] [data-form-error]:not([hidden])');
        if (err && err.textContent.trim()) {
          err.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
          return;
        }
        if (Date.now() - t0 < 1200) setTimeout(findErr, 100);
      };
      setTimeout(findErr, 60);
    });
    bar.querySelector('[data-sc-bar-open]').addEventListener('click', () => open('bar'));
    bar.querySelector('[data-sc-bar-mini]')?.addEventListener('click', () => open('bar'));
    bar.querySelector('[data-sc-bar-pay]').addEventListener('click', () => {
      if (payBtn.disabled || payBtn.classList.contains('ga-guard-locked')) {
        open('bar'); // the guard message is shown above the drawer's checkout button
        return;
      }
      payBtn.click();
    });
    if (livePriceEl && 'MutationObserver' in window) {
      new MutationObserver(() => syncBar()).observe(livePriceEl, { attributes: true, attributeFilter: ['data-price'] });
    }
  }

  /* ---------- Cart events ---------- */
  document.addEventListener('cart:updated', (e) => {
    if (selfEmit) return;
    const d = e.detail;
    if (d && Array.isArray(d.items) && 'item_count' in d && 'total_price' in d) setCart(d);
    // An add is in flight: cart:confirm renders from the /cart/add.js response, one background check later
    else if (pending || (lastAdd && Date.now() - lastAdd.at < 3000)) reconcileSoon();
    else refresh();
  });
  // Back / Forward from the bfcache, or a tab coming back: re-read the cart (count, bar, drawer). Skipped while a
  // quantity change is still queued, which refreshes on its own.
  const resync = () => {
    if (!qtyQueue.size) refresh();
  };
  addEventListener('pageshow', (e) => {
    if (e.persisted) resync();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') resync();
  });

  const start = () => {
    refresh().then((c) => {
      log('cart', c);
      if (c && catalog && Brain()) Brain().creditAdds(c);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
